import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Plus, Trash2, Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  ["yes_no", "Yes / No"], ["pass_fail", "Pass / Fail"], ["rating_5", "Rating 1-5"], ["rating_10", "Rating 1-10"],
  ["percentage", "Percentage"], ["multiple_choice", "Multiple Choice"], ["dropdown", "Dropdown"], ["text", "Text"],
  ["numeric", "Numeric"], ["date", "Date"], ["time", "Time"], ["image", "Image"], ["signature", "Signature"],
];

const newQuestion = () => ({ id: crypto.randomUUID(), text: "", type: "yes_no", weight: 10, critical: false, mandatory: true, requires_photo: false, options: [], guideline: "" });
const newSection = () => ({ id: crypto.randomUUID(), title: "New Section", weight: 1, department: "General", questions: [newQuestion()] });

export default function TemplateBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [tpl, setTpl] = useState(isNew ? { name: "", audit_category: "General", description: "", sections: [newSection()] } : null);
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/meta").then((r) => setDepartments(r.data.departments));
    if (!isNew) api.get(`/templates/${id}`).then((r) => setTpl(r.data)).catch(() => toast.error("Template not found"));
  }, [id, isNew]);

  if (!tpl) return <div className="flex justify-center py-24"><div className="w-8 h-8 border-2 border-[#14532D] border-t-transparent rounded-full animate-spin" /></div>;

  const upSection = (si, patch) => setTpl({ ...tpl, sections: tpl.sections.map((s, i) => (i === si ? { ...s, ...patch } : s)) });
  const upQuestion = (si, qi, patch) => upSection(si, { questions: tpl.sections[si].questions.map((q, i) => (i === qi ? { ...q, ...patch } : q)) });

  const save = async () => {
    if (!tpl.name.trim()) return toast.error("Template name is required");
    setSaving(true);
    try {
      const body = { name: tpl.name, audit_category: tpl.audit_category, description: tpl.description, sections: tpl.sections };
      if (isNew) {
        const { data } = await api.post("/templates", body);
        toast.success("Template created");
        navigate(`/templates/${data.id}/edit`, { replace: true });
      } else {
        await api.put(`/templates/${id}`, body);
        toast.success("Template saved (new version)");
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="template-builder-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/templates")} data-testid="builder-back"><ArrowLeft className="w-4 h-4" /></Button>
          <h1 className="text-2xl font-extrabold tracking-tight text-stone-900">{isNew ? "New Template" : "Edit Template"}</h1>
          {!isNew && <span className="text-xs font-mono text-stone-400">v{tpl.version}</span>}
        </div>
        <Button data-testid="template-save-button" onClick={save} disabled={saving} className="bg-[#14532D] hover:bg-[#166534]">
          <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Template"}
        </Button>
      </div>

      <div className="bg-white border border-[#E7E5DF] rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-stone-500">Template Name</label>
          <Input data-testid="template-name-input" className="mt-1.5" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} placeholder="e.g. Housekeeping Audit" />
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-stone-500">Category</label>
          <Input data-testid="template-category-input" className="mt-1.5" value={tpl.audit_category} onChange={(e) => setTpl({ ...tpl, audit_category: e.target.value })} placeholder="e.g. Brand, Environmental, Food Safety" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-medium uppercase tracking-wider text-stone-500">Description</label>
          <Textarea data-testid="template-description-input" className="mt-1.5" rows={2} value={tpl.description} onChange={(e) => setTpl({ ...tpl, description: e.target.value })} />
        </div>
      </div>

      <Accordion type="multiple" defaultValue={tpl.sections.slice(0, 1).map((s) => s.id)} className="space-y-4">
        {tpl.sections.map((s, si) => (
          <AccordionItem key={s.id} value={s.id} className="bg-white border border-[#E7E5DF] rounded-xl px-5 border-b">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3 text-left">
                <span className="w-6 h-6 rounded bg-[#D1FAE5] text-[#14532D] text-xs font-bold flex items-center justify-center">{si + 1}</span>
                <span className="font-semibold text-stone-800">{s.title || "Untitled section"}</span>
                <span className="text-xs text-stone-400 font-mono">{s.questions.length} q · w{s.weight}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pb-5">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <label className="text-[11px] uppercase tracking-wider text-stone-500">Section title</label>
                  <Input data-testid={`section-title-${si}`} className="mt-1" value={s.title} onChange={(e) => upSection(si, { title: e.target.value })} />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-stone-500">Department</label>
                  <Select value={s.department} onValueChange={(v) => upSection(si, { department: v })}>
                    <SelectTrigger className="mt-1" data-testid={`section-dept-${si}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-stone-500">Section weight</label>
                  <Input type="number" min="0.1" step="0.5" className="mt-1" value={s.weight} onChange={(e) => upSection(si, { weight: parseFloat(e.target.value) || 1 })} />
                </div>
              </div>

              <div className="space-y-3">
                {s.questions.map((q, qi) => (
                  <div key={q.id} className="border border-stone-200 rounded-lg p-4 bg-stone-50/50 space-y-3">
                    <div className="flex gap-3">
                      <Textarea data-testid={`question-text-${si}-${qi}`} rows={1} className="flex-1 bg-white min-h-[38px]" placeholder="Question text..." value={q.text} onChange={(e) => upQuestion(si, qi, { text: e.target.value })} />
                      <button className="p-2 h-fit rounded hover:bg-rose-50 text-rose-500" onClick={() => upSection(si, { questions: s.questions.filter((_, i) => i !== qi) })} data-testid={`question-delete-${si}-${qi}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                      <Select value={q.type} onValueChange={(v) => upQuestion(si, qi, { type: v })}>
                        <SelectTrigger className="w-36 h-8 text-xs bg-white" data-testid={`question-type-${si}-${qi}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                      <div className="flex items-center gap-1.5 text-xs text-stone-600">
                        Weight <Input type="number" min="0" className="w-16 h-8 bg-white" value={q.weight} onChange={(e) => upQuestion(si, qi, { weight: parseFloat(e.target.value) || 0 })} data-testid={`question-weight-${si}-${qi}`} />
                      </div>
                      <label className="flex items-center gap-1.5 text-xs text-stone-600">
                        <Switch checked={q.critical} onCheckedChange={(v) => upQuestion(si, qi, { critical: v })} data-testid={`question-critical-${si}-${qi}`} />
                        <span className={q.critical ? "text-rose-600 font-semibold" : ""}>Critical</span>
                      </label>
                      <label className="flex items-center gap-1.5 text-xs text-stone-600">
                        <Switch checked={q.mandatory} onCheckedChange={(v) => upQuestion(si, qi, { mandatory: v })} /> Mandatory
                      </label>
                      <label className="flex items-center gap-1.5 text-xs text-stone-600">
                        <Switch checked={q.requires_photo} onCheckedChange={(v) => upQuestion(si, qi, { requires_photo: v })} /> Photo required
                      </label>
                    </div>
                    {["multiple_choice", "dropdown"].includes(q.type) && (
                      <Input className="bg-white text-xs" placeholder="Options, comma-separated (e.g. Excellent, Good, Poor)" value={q.options.join(", ")} onChange={(e) => upQuestion(si, qi, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })} />
                    )}
                    <Input className="bg-white text-xs" placeholder="Auditor guideline (how to check this point)" value={q.guideline || ""} onChange={(e) => upQuestion(si, qi, { guideline: e.target.value })} />
                  </div>
                ))}
                <div className="flex gap-3">
                  <Button variant="outline" size="sm" onClick={() => upSection(si, { questions: [...s.questions, newQuestion()] })} data-testid={`add-question-${si}`}>
                    <Plus className="w-3.5 h-3.5 mr-1.5" />Add Question
                  </Button>
                  <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => setTpl({ ...tpl, sections: tpl.sections.filter((_, i) => i !== si) })} data-testid={`section-delete-${si}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete Section
                  </Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <button
        data-testid="add-section-button"
        onClick={() => setTpl({ ...tpl, sections: [...tpl.sections, newSection()] })}
        className="w-full border-2 border-dashed border-stone-300 rounded-xl py-4 text-sm text-stone-500 hover:border-[#14532D] hover:text-[#14532D] hover:bg-emerald-50/30 transition-colors"
      >
        + Add Section
      </button>
    </div>
  );
}
